import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../store/store";
import PurchaseLayout from "./components/PurchaseLayout";
import { getAuthListing, getListing } from "../../../../../apis/listings/buyer";
import {
  DownloadIcon,
  HnaLogo,
} from "../../../../common/components/LayoutIcons";
import ReviewAndConfirmModal from "./components/ReviewAndConfirmModal";
import { useQuery } from "@tanstack/react-query";
import { getPaymentInvoiceData } from "../../../../../apis/buyer/payment";
import { DisplayAmount } from "../../../../common/components/CurrencySwitch";
import { getDownloadFile } from "../../../../../apis/others";
import { fetchAuthListingSuccess } from "../../../../../store/slices/buyer/listingsSlice";

let userData = JSON.parse(localStorage.getItem("user") || "{}");

const PurchaseInvoice = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { referenceId } = useParams();
  const listing = useSelector((state: RootState) => state.listings);
  const [showReviewAndConfirmModal, setShowReviewAndConfirmModal] =
    useState<number>();
  const [downloadUrl, setDownloadUrl] = useState("");

  const { data: paymentInvoiceData, isLoading: isInvoiceDataLoading } =
    useQuery({
      queryKey: ["paymentInvoiceData", { referenceId: referenceId }],
      queryFn: getPaymentInvoiceData,
    });

  const { data: listingData, isLoading: isListingLoading } = useQuery({
    queryKey: [
      "listing",
      { listingId: paymentInvoiceData?.listing?.lid, getRef: true },
    ],
    queryFn: getAuthListing,
  });

  useEffect(() => {
    if (listingData?.lid && listingData?.lid !== listing.listing?.lid) {
      dispatch(fetchAuthListingSuccess(listingData));
    }
  }, [listingData?.lid]);

  const {
    data: pdfBlob,
    isSuccess,
    refetch,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["pdf", paymentInvoiceData?.download_invoice_url],
    queryFn: () =>
      getDownloadFile(paymentInvoiceData?.download_invoice_url || ""),
    enabled: false, // don’t auto-fetch
  });

  // Open PDF in new tab when data is received
  useEffect(() => {
    if (isSuccess && pdfBlob instanceof Blob) {
      const fileURL = URL.createObjectURL(pdfBlob);

      window.open(fileURL);
      // Optional cleanup:
      URL.revokeObjectURL(fileURL);
    }
  }, [isSuccess, pdfBlob]);

  return (
    <PurchaseLayout heading="Purchase invoice">
      <>
        <div className="card-shadow flex-1 flex flex-col sm:min-w-[700px] rounded-[8px] relative justify-between">
          <button
            onClick={() => refetch()}
            disabled={
              isInvoiceDataLoading ||
              !paymentInvoiceData?.download_invoice_url ||
              isFetching
            }
            className="filled-button absolute top-[-75px] right-[0] text-[16px] p-y-[12px] p-x-[26px] hidden sm:flex items-center gap-x-[10px]"
            style={{ height: "unset", fontWeight: 500 }}
          >
            <DownloadIcon />
            Download
          </button>
          <div
            className="filled-button font-[#FFFFFF] absolute top-[-70px] right-[0] text-[16px] flex items-center justify-center gap-x-[10px] sm:hidden"
            style={{
              height: "unset",
              padding: 10,
              fontWeight: 500,
              ...(isInvoiceDataLoading ||
              !paymentInvoiceData?.download_invoice_url ||
              isFetching
                ? { display: "none" }
                : {}),
            }}
            onClick={() => refetch()}
          >
            <DownloadIcon />
          </div>
          <div className="PDF">
            <div
              style={{
                backgroundColor: "#066D0E",
                padding: 23,
                borderRadius: "10px 10px 0 0",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <HnaLogo />
            </div>
            <div
              style={{
                backgroundColor: "#CECECE26",
                color: "#066D0E",
                padding: 16,
                fontSize: 18,
                textAlign: "center",
              }}
            >
              Home and Abroad Property Purchase Invoice
            </div>
            {isInvoiceDataLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 30,
                  letterSpacing: "1px",
                  fontStyle: "italic",
                }}
              >
                Loading...
              </div>
            ) : !paymentInvoiceData?.download_invoice_url ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 30,
                  letterSpacing: "1px",
                  fontStyle: "italic",
                  opacity: 0.5,
                }}
              >
                No invoice data found
              </div>
            ) : (
              <div className="flex flex-col gap-y-[50px] pt-[5px] pb-[31px] px-[15px] sm:px-[55px]">
                <div>
                  <div
                    style={{
                      color: "#066D0E",
                      fontSize: 18,
                      fontWeight: 400,
                      marginBottom: 16,
                    }}
                  >
                    Bill To
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#363636",
                      fontSize: 14,
                      fontWeight: 300,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div>
                        {paymentInvoiceData?.name_as_expected_on_property_doc}
                      </div>
                      <div>{paymentInvoiceData?.address}</div>
                      <div>
                        <a
                          href={`mailto:${userData?.email}`}
                          style={{
                            color: "#066D0E",
                            textDecoration: "underline",
                            wordBreak: "break-all",
                          }}
                        >
                          {userData?.email}
                        </a>
                      </div>
                      <div>{userData?.phone_number}</div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div>
                        <b>Invoice No.</b>: {paymentInvoiceData?.invoice_no}
                      </div>
                      <div>
                        <b>Date</b>: {paymentInvoiceData?.issue_date}
                      </div>
                      <div>
                        <b>Due Date</b>: {paymentInvoiceData?.due_date}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: 10,
                      color: "#363636",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            width: "70%",
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                          }}
                        >
                          Description
                        </td>
                        <td
                          style={{
                            width: "30%",
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                            textAlign: "right",
                          }}
                        >
                          Amount (NGN)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                          }}
                        >
                          {paymentInvoiceData?.listing?.name}
                          <br />
                          <br />
                          Address:{" "}
                          <span style={{ fontWeight: 300 }}>
                            {paymentInvoiceData?.listing?.full_address}
                          </span>
                          <br />
                          Listing ID:{" "}
                          <span style={{ fontWeight: 300 }}>
                            {paymentInvoiceData?.listing?.listing_code}
                          </span>
                          <br />
                          Property Cost
                        </td>
                        <td
                          style={{
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                            textAlign: "right",
                          }}
                        >
                          <DisplayAmount
                            amount={paymentInvoiceData?.listing?.price || ""}
                            format="string"
                          />
                        </td>
                      </tr>
                      {paymentInvoiceData?.additional_charges.map(
                        (charge, index) => (
                          <tr key={index}>
                            <td
                              style={{
                                border: "1px solid #cccccc",
                                padding: "20px",
                                verticalAlign: "bottom",
                              }}
                            >
                              {`${charge.name} ${charge.value}${
                                charge.type === "percentage" ? "%" : ""
                              }`}
                            </td>
                            <td
                              style={{
                                border: "1px solid #cccccc",
                                padding: "20px",
                                verticalAlign: "bottom",
                                textAlign: "right",
                              }}
                            >
                              <DisplayAmount
                                amount={charge.amount || ""}
                                format="string"
                              />
                            </td>
                          </tr>
                        )
                      )}
                      <tr>
                        <td
                          style={{
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                          }}
                        >
                          Total Payable
                        </td>
                        <td
                          style={{
                            border: "1px solid #cccccc",
                            padding: "20px",
                            verticalAlign: "bottom",
                            textAlign: "right",
                          }}
                        >
                          <DisplayAmount
                            amount={paymentInvoiceData?.total_payable || ""}
                            format="string"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <div
                    style={{
                      // color: "#066D0E",
                      fontSize: 18,
                      fontWeight: 400,
                      marginBottom: 16,
                    }}
                  >
                    Payment Link
                  </div>
                  <div>
                    <a
                      href={`${process.env.REACT_APP_FE_BASEURL}/purchase/checkout/${listing.listing?.lid}`}
                      style={{
                        color: "#066D0E",
                        textDecoration: "underline",
                        wordBreak: "break-all",
                      }}
                    >
                      {`${process.env.REACT_APP_FE_BASEURL}/purchase/checkout/${listing.listing?.lid}`}
                    </a>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 300,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      color: "#066D0E",
                      fontSize: 18,
                      fontWeight: 400,
                    }}
                  >
                    Note:
                  </div>
                  <div>
                    <ul style={{ listStyle: "disc", paddingLeft: 40 }}>
                      <li style={{ paddingLeft: 10, marginBottom: 5 }}>
                        Payment should be made within 3 days of receiving
                        invoice.
                      </li>
                      <li style={{ paddingLeft: 10 }}>
                        Contact{" "}
                        <a
                          href="mailto:info@homeandabroad.ng"
                          style={{
                            color: "#066D0E",
                            textDecoration: "underline",
                          }}
                        >
                          info@homeandabroad.ng
                        </a>{" "}
                        for any queries.
                      </li>
                    </ul>
                  </div>
                  <div>
                    For more information and enquiries please feel free to reach
                    out to us on our website{" "}
                    <a
                      href="www.homeandabroad.ng"
                      style={{ color: "#066D0E", textDecoration: "underline" }}
                    >
                      www.homeandabroad.ng
                    </a>{" "}
                    or send an email to{" "}
                    <a
                      href="mailto:info@homeandabroad.ng"
                      style={{
                        color: "#066D0E",
                        textDecoration: "underline",
                      }}
                    >
                      info@homeandabroad.ng
                    </a>
                    , our team is always happy to help you!
                  </div>
                </div>
              </div>
            )}
          </div>
          {!isInvoiceDataLoading &&
            !!paymentInvoiceData?.download_invoice_url && (
              <button
                className="filled-button w-full"
                style={{ borderRadius: "0 0 0.5rem 0.5rem" }}
                onClick={() => setShowReviewAndConfirmModal(Math.random())}
              >
                Continue
              </button>
            )}
          <ReviewAndConfirmModal
            show={showReviewAndConfirmModal}
            btnActtion={() =>
              navigate(`/purchase/checkout/${listing.listing?.lid}`)
            }
          />
        </div>
      </>
    </PurchaseLayout>
  );
};

export default PurchaseInvoice;
